<template>
  <el-dialog
    v-model="panelVisible"
    :title="title"
    :width="width"
    :close-on-click-modal="false"
    append-to-body
    class="form-dialog"
 >
    <FormCreate
      v-if="rule"
      v-model="value"
      v-model:api="fApi"
      :rule="mixinRule"
      :option="option"
   />

    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="handleClosePanel">取消</el-button>
        <el-button size="small" type="primary" :loading="submitLoading" @click="handleConfirm">确定</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue';
import formCreate, { type Api, type Rule, type Options } from '@form-create/element-ui';
import { getUploadProps } from '@/utils/form-create';
import FormDialogTip from './formDialogTip.vue';

defineOptions({ name: 'FormDialog' });

formCreate.component('FormDialogTip', FormDialogTip);
const FormCreate = formCreate.$form();

const props = withDefaults(
  defineProps<{
    width?: string;
 }>(),
  { width: '500px' },
);

const emit = defineEmits<{
 (e: 'update', payload: [Record<string, unknown>, Record<string, unknown> | undefined]): void;
}>();

const title = ref('');
const value = ref<Record<string, unknown>>({});
const rule = ref<Rule[] | null>(null);
const action = ref<
 ((form: Record<string, unknown>) => boolean | void | Promise<boolean | void>) | null
>(null);
const fApi = shallowRef<Api | null>(null);
const panelVisible = ref(false);
const submitLoading = ref(false);

const baseFormOptions = (): Options => ({
  form: {
    size: 'small',
    labelWidth: '7em',
  },
  submitBtn: {
    show: false,
  },
});

const option = computed(() => {
  const baseOptions = baseFormOptions();
  const r = rule.value;
  if (!r) return baseOptions;

  let maxFormLabelCharCount = 0;
  for (const item of r) {
    const t = (item as { title?: string }).title;
    if (t && t.length > maxFormLabelCharCount) {
      maxFormLabelCharCount = t.length;
    }
  }
  baseOptions.form!.labelWidth = `${maxFormLabelCharCount + 2}em`;
  return baseOptions;
});

const mixinRule = computed(() => {
  const r = rule.value;
  if (!r) return [];
  return r.map((item) => {
    if ((item as { type?: string }).type === 'upload') {
      return {
        ...item,
        props: {
          ...getUploadProps(),
          ...(item as { props?: Record<string, unknown> }).props,
        },
      };
    }
    return item;
  });
});

watch(
  value,
  (newVal, oldVal) => {
    emit('update', [newVal, oldVal]);
  },
  { deep: true },
);

function resetFormState() {
  title.value = '';
  value.value = {};
  rule.value = null;
  action.value = null;
  fApi.value = null;
}

async function handleConfirm() {
  if (submitLoading.value) return;
  submitLoading.value = true;
  try {
    await fApi.value?.validate();
  } catch {
    submitLoading.value = false;
    return;
  }
  const fn = action.value;
  if (!fn) {
    submitLoading.value = false;
    return;
  }
  const isSuccess = await fn(value.value);
  submitLoading.value = false;
  if (isSuccess) {
    handleClosePanel();
  }
}

function handleClosePanel() {
  panelVisible.value = false;
  setTimeout(() => {
    resetFormState();
  }, 200);
}

function open(opts: {
  rule: Rule[];
  title: string;
  action: (form: Record<string, unknown>) => boolean | void | Promise<boolean | void>;
}) {
  title.value = opts.title;
  rule.value = opts.rule;
  action.value = opts.action;
  panelVisible.value = true;
}

defineExpose({
  /** 供父级 `ref.fApi` 调用 form-create API（与 Options API 子组件一致） */
  get fApi() {
    return fApi.value;
  },
  open,
  handleClosePanel,
});
</script>

<style lang="scss">
.form-dialog .has-tips + .el-form-item__error {
  position: static;
}
</style>

<style scoped lang="scss">
:deep(._fc-upload) {
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
}
</style>
